/**
 * Interpret the cdm-mw batch result returned by POST/DELETE /api/mm-appointments.
 *
 * The endpoint returns a 2xx status even when some rows fail (207 Multi-Status),
 * so callers cannot rely on the catch() path to surface per-row failures — they
 * must inspect the {successes, errors} payload here. (TOP-5683 / CS 11009 §11009.19)
 *
 * @param {Object} [batchResult] - MmAppointmentBatchResult ({successes, errors, message})
 * @param {number} [attemptedCount] - rows submitted, used for the "M of N" summary
 * @returns {{successCount: number, errorCount: number, total: number, message: ?string, errorLines: Array<{mmid: string, symbol: string, mmType: *, label: string, message: string}>}}
 */
export const summarizeBatchResult = (batchResult, attemptedCount) => {
    const successes = (batchResult && batchResult.successes) || [];
    const errors = (batchResult && batchResult.errors) || [];
    const total = typeof attemptedCount === 'number'
        ? attemptedCount
        : successes.length + errors.length;

    return {
        successCount: successes.length,
        errorCount: errors.length,
        total,
        // GBO's per-request Summary message (e.g. "...processed for next day trading"), passed
        // through by cdm-mw. Null when GBO returned no summary — callers fall back to generic text.
        message: (batchResult && batchResult.message) || null,
        // mmid/symbol/mmType are preserved (not just the joined label) so callers
        // can match a failure back to the exact row it came from — e.g. to narrow a
        // retry to only the rows that failed. mmType is part of the key because the
        // same mmid+symbol can carry more than one MM type, so mmid+symbol alone is
        // not unique.
        errorLines: errors.map((error) => ({
            mmid: error.mmid,
            symbol: error.symbol,
            mmType: error.mmType,
            label: [error.mmid, error.symbol].filter(Boolean).join(' / '),
            message: error.message || 'Request failed'
        }))
    };
};

/**
 * Build a lookup of MM-type self-service eligibility keyed by the GBO display
 * label.
 *
 * The /api/mm-types option `value` is a registry KEY (e.g. SLMM = 8) that does
 * NOT equal the grid row's RDL `mmType` code (e.g. SLMM = 28) — see TOP-5680 —
 * so the numeric code cannot join an option to a row. The one field both sides
 * share is the GBO label: `gboLabel` on the option, `mmTypeLabel` on the row.
 *
 * @param {Array<{gboLabel?: string, eligible?: boolean}>} [mmTypeOptions]
 * @returns {Object<string, boolean>} gboLabel -> isEligible
 */
export const buildMmTypeEligibilityByLabel = (mmTypeOptions = []) => {
    const map = {};
    mmTypeOptions.forEach((option) => {
        if (option && option.gboLabel) {
            // `!== false` mirrors MMTypesSelect: treat a missing flag as eligible
            // so an older backend doesn't lock everything out.
            map[option.gboLabel] = option.eligible !== false;
        }
    });
    return map;
};

/**
 * Whether a grid row may be selected for self-service delete. Rows whose MM type
 * is NOT self-service eligible (e.g. CRS-managed SLMM/DMM) are locked out of
 * selection so the user can't queue a delete the backend would only reject
 * ("not eligible for self-service. Contact CRS"). An unknown label stays
 * selectable — fail-open, since the server still backstops the delete.
 *
 * @param {{mmTypeLabel?: string}} [row] - grid row data
 * @param {Object<string, boolean>} [eligibilityByLabel] - from buildMmTypeEligibilityByLabel
 * @returns {boolean}
 */
export const isMmAppointmentRowSelectable = (row, eligibilityByLabel = {}) => {
    const label = row && row.mmTypeLabel;
    return label == null || eligibilityByLabel[label] !== false;
};

/**
 * Positions of entries that repeat an earlier entry's MMID + symbol.
 *
 * CS 11009 §11009.6.3: an Add or Delete array may not carry more than one entry for the
 * same symbol and MMID, so the repeat is caught here and pointed at its row
 * (TOP-5791). Catching it here lets the modals name the offending rows instead of surfacing
 * one request-level error the user has to map back themselves.
 *
 * MM type is deliberately NOT part of the key: a GBO appointment row is keyed on
 * (crd, mmid, symbol) with mm_type as an overwriteable attribute, so two entries differing
 * only by type are one appointment written twice, not two appointments.
 *
 * Only the repeats are returned, so the first occurrence stays clean and the user has a row
 * to keep.
 *
 * @param {Array<{mmid: *, symbol: *}>} [appointments]
 * @returns {Set<number>} indexes into `appointments`
 */
export const findDuplicateAppointmentIndexes = (appointments = []) => {
    const seen = new Set();
    const duplicates = new Set();

    appointments.forEach((appointment, index) => {
        const mmid = String((appointment && appointment.mmid) || '').trim().toUpperCase();
        const symbol = String((appointment && appointment.symbol) || '').trim().toUpperCase();
        const key = `${mmid}|${symbol}`;

        if (seen.has(key)) {
            duplicates.add(index);
        } else {
            seen.add(key);
        }
    });

    return duplicates;
};
